/**
 * 双向语音控制坞：教师端与辩手端共用。
 *
 * - 启用采集：getUserMedia + 向每个 peer 发布音轨；
 * - 麦克风/摄像头开关：只控制本端发送；
 * - 静音收听：只影响远端 <audio>，不停止采集；
 * - 显示已接收的远端音频路数，让“是否真的在双向”可验证。
 */
import { useState } from 'react';
import { useArena } from '../../state/ArenaContext.tsx';
import { Button } from '../ui/primitives.tsx';
import { AudioDeviceLab } from './AudioDeviceLab.tsx';

export function MediaDock({
  prefix = 'hud',
  compact = false,
}: {
  prefix?: 'hud' | 'coach';
  compact?: boolean;
}) {
  const { state, actions } = useArena();
  const { peering } = state;
  const [deviceLabOpen, setDeviceLabOpen] = useState(false);
  const remoteCount = actions.getRemoteStreams().length;
  const live = peering.mediaEnabled && remoteCount > 0;

  return (
    <>
    <div
      data-testid={prefix === 'hud' ? 'hud-media-controls' : `${prefix}-media-dock`}
      className={`lens flex flex-wrap items-center gap-x-2 gap-y-1.5 ${
        compact ? 'px-2.5 py-2' : 'p-3'
      }`}
    >
      <span className="flex items-center gap-1.5">
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            live ? 'bg-emerald-300 shadow-[0_0_8px_rgba(134,239,172,0.8)]' : 'bg-slate-500'
          }`}
        />
        <span className="micro-label">VOICE</span>
        <span
          className="mono text-[10px] text-slate-400"
          data-testid={`${prefix}-remote-count`}
          data-remote-count={remoteCount}
        >
          {live ? `双向 · ${remoteCount} 路` : peering.mediaEnabled ? '等待对方' : '未启用'}
        </span>
      </span>

      <span className="mx-1 h-4 w-px bg-white/10" />

      <Button
        size="sm"
        variant={peering.mediaEnabled ? 'secondary' : 'primary'}
        testId={`${prefix}-enable-media`}
        onClick={() => void actions.enableMedia()}
        title="采集麦克风并建立 P2P 音视频直连；同时启动端侧 Silero VAD"
      >
        {peering.mediaEnabled ? '重新启用媒体' : '启用麦克风/媒体'}
      </Button>
      <Button
        size="sm"
        variant={peering.micEnabled ? 'secondary' : 'ghost'}
        testId={`${prefix}-toggle-mic`}
        onClick={() => void actions.setMicrophoneEnabled(!peering.micEnabled)}
        title="开关本端麦克风发送"
      >
        麦克风{peering.micEnabled ? '开' : '关'}
      </Button>
      <Button
        size="sm"
        variant={peering.cameraEnabled ? 'secondary' : 'ghost'}
        testId={`${prefix}-toggle-camera`}
        onClick={() => void actions.setCameraEnabled(!peering.cameraEnabled)}
        title="开关本端摄像头（视频为可选）"
      >
        摄像头{peering.cameraEnabled ? '开' : '关'}
      </Button>
      <Button
        size="sm"
        variant={peering.deafened ? 'danger' : 'ghost'}
        testId={`${prefix}-toggle-deafen`}
        onClick={() => actions.setDeafened(!peering.deafened)}
        title="静音收听：不停止采集，只关闭远端音频播放"
      >
        {peering.deafened ? '收听已静音' : '静音收听'}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        testId={`${prefix}-device-lab`}
        onClick={() => setDeviceLabOpen(true)}
        title="枚举本机麦克风/扬声器，测试输入电平与输出测试音"
      >
        设备自检
      </Button>
    </div>
    {deviceLabOpen && <AudioDeviceLab onClose={() => setDeviceLabOpen(false)} />}
    </>
  );
}
